import { dehydrate, hydrate, type QueryClient } from '@tanstack/react-query';

/**
 * Persists the TanStack Query cache to localStorage across full browser page loads.
 *
 * Why this exists: the yellow DOKA header (logo, avatar) comes from the private
 * `@uag/react-core` BaseLayout/portal chrome, not our own code (see HeaderBrand.tsx). Clicking
 * the logo navigates back to "/" via a plain full-page browser navigation, not a client-side
 * react-router transition - which throws away the in-memory QueryClient entirely and forces
 * every catalog/tree/config query to refetch from scratch, even though queries.ts's own
 * `staleTime` (5 min - Infinity, see App.tsx's `refetchOnMount: false` too) says the data is
 * still perfectly fresh. That existing config already prevents redundant refetching WITHIN one
 * page session; this covers the case where a brand new page session starts with an empty cache.
 *
 * Deliberately hand-rolled on the core `dehydrate`/`hydrate` primitives (already part of
 * `@tanstack/react-query` - no new dependency) rather than pulling in the separate
 * `@tanstack/react-query-persist-client` package.
 */

const STORAGE_KEY = 'pih-query-cache-v1';
// A persisted cache from a previous deploy could carry a shape queries.ts's queryFns no longer
// produce (a field renamed, an endpoint reshaped) - keyed off APP_VERSION (vite.config.ts's
// `define`, ultimately package.json's version) so any version mismatch is treated as "nothing
// persisted" rather than risking a stale/malformed cache silently reaching the UI after a deploy.
const STORAGE_VERSION = typeof APP_VERSION === 'string' ? APP_VERSION : 'dev';
// How long a persisted cache is even attempted to be restored from, independent of each query's
// own staleTime (which still governs whether the restored data triggers a background refetch) -
// a guard against a browser tab/profile left untouched for days before the next full reload.
const MAX_AGE_MS = 24 * 60 * 60 * 1000;

/**
 * Which queries are persisted at all - an ALLOWLIST, not everything the cache holds.
 *
 * The catalog index is a five-figure number of articles; serialising it into localStorage means a
 * JSON.stringify of tens of megabytes against a ~5 MB quota, i.e. a few hundred milliseconds of
 * blocked main thread on every tab switch (see persistQueryCacheOnUnload's `visibilitychange`) for
 * a write that then throws QuotaExceededError and is swallowed - all cost, no benefit. What this
 * mechanism was actually built for are the small, long-lived documents (config, tree, labels,
 * attribute mapping, detail layout) that make a full page load feel instant; the index refetches
 * on its own and is the one thing worth its own request.
 *
 * Matched by prefix, so ['catalog','labels','de'] is covered by ['catalog','labels'].
 */
const PERSISTED_QUERY_KEYS: string[][] = [
    ['catalog', 'config'],
    ['catalog', 'detail-layout'],
    ['catalog', 'attribute-mapping'],
    ['catalog', 'labels'],
    ['catalog', 'tree']
];

/**
 * Hard ceiling for what is written, after serialisation. The allowlist above already keeps the big
 * payloads out, but tree.json grows with the catalog (it carries every system's article numbers),
 * so this is the backstop that keeps a future delivery from quietly reintroducing the very problem
 * the allowlist solves. Well under the ~5 MB browsers typically grant a whole origin.
 */
const MAX_PERSISTED_BYTES = 2 * 1024 * 1024;

const isPersistable = (queryKey: readonly unknown[]): boolean =>
    PERSISTED_QUERY_KEYS.some((prefix) => prefix.every((segment, index) => queryKey[index] === segment));

interface PersistedCache {
    version: string;
    timestamp: number;
    state: unknown;
}

/**
 * Restores a previously persisted cache into a freshly created QueryClient, if one exists, isn't
 * expired, and matches the current app version. Call this once, synchronously, right after
 * creating the QueryClient and before the first render (see App.tsx) - hydrate() is synchronous,
 * so components can mount already seeing the restored data instead of a loading state.
 */
export const restorePersistedQueryCache = (queryClient: QueryClient) => {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) {
            return;
        }
        const persisted = JSON.parse(raw) as PersistedCache;
        if (persisted.version !== STORAGE_VERSION || Date.now() - persisted.timestamp > MAX_AGE_MS) {
            localStorage.removeItem(STORAGE_KEY);
            return;
        }
        hydrate(queryClient, persisted.state);
    } catch {
        // Corrupt/foreign localStorage content, or a browser blocking storage access entirely
        // (e.g. a private window) - fall back to the normal empty-cache startup rather than crash.
        localStorage.removeItem(STORAGE_KEY);
    }
};

const persistNow = (queryClient: QueryClient) => {
    try {
        const state = dehydrate(queryClient, {
            // Only queries that actually finished loading successfully - a still-pending or
            // errored query has nothing worth restoring, and persisting an in-flight one could
            // hydrate a query that's permanently stuck pending on the next load - and only the
            // small ones (see PERSISTED_QUERY_KEYS).
            shouldDehydrateQuery: (query) => query.state.status === 'success' && isPersistable(query.queryKey),
        });
        const payload: PersistedCache = { version: STORAGE_VERSION, timestamp: Date.now(), state };
        const serialized = JSON.stringify(payload);
        if (serialized.length > MAX_PERSISTED_BYTES) {
            // Don't leave a stale, smaller cache behind pretending to be current.
            localStorage.removeItem(STORAGE_KEY);
            return;
        }
        localStorage.setItem(STORAGE_KEY, serialized);
    } catch {
        // Storage full/unavailable - persistence is a pure optimization, never worth surfacing.
    }
};

/**
 * Wires up saving the cache right before the page actually goes away. `pagehide` fires reliably
 * for the full-page navigation this is meant to cover (unlike `beforeunload`, which mobile
 * Safari can skip entirely); `visibilitychange` -> hidden is a second safety net for the same
 * reason (tab switch/app backgrounding with no unload event at all). Call this once after
 * creating the QueryClient.
 */
export const persistQueryCacheOnUnload = (queryClient: QueryClient) => {
    const save = () => persistNow(queryClient);
    window.addEventListener('pagehide', save);
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') {
            save();
        }
    });
};
